import axiosClient from "axios";

import { setupCache } from "axios-cache-interceptor";
import telenorid from "../telenorid/telenorid";

import adapterHelper from "@/integrations/adapters/adapterHelper";

import { useUserStore } from "~/stores/platform/user/user.ts";

let requests = [];

let reauthPromise;

export default function (baseURL) {
	let apiUrl = adapterHelper.apiUrl();

	const axios = axiosClient.create({
		baseURL: apiUrl + baseURL,
		withCredentials: true,
	});

	if (import.meta.client) {
		setupCache(axios, {
			ttl: 15 * 60 * 1000, // 15 minutes,
			exclude: {
				query: false,
			},
			cacheTakeover: false, // TODO: Remove when Apigee fixes cors pragma issue
		});
	}

	axios.defaults.withCredentials = true;

	const INTERVAL_MS = 10;

	// Prevent multiple parallel requests to the exact same resource
	axios.interceptors.request.use(function (conf) {
		return new Promise((resolve) => {
			let interval = setInterval(() => {
				if (!requests.includes(getKey(conf))) {
					requests.push(getKey(conf));
					clearInterval(interval);
					resolve(conf);
				}
			}, INTERVAL_MS);
		});
	});

	axios.interceptors.request.use(
		async (request) => {
			let user = await telenorid.getOrLoginUser();

			if (user) {
				request.headers.Authorization = "Bearer " + user.access_token;
			} else {
				// Cancel request`
				return {
					...request,
					cancelToken: new axiosClient.CancelToken((cancel) =>
						cancel("Cancelled request because no authentication token is present. URL: " + request.url),
					),
				};
			}

			return request;
		},
		(error) => {
			return Promise.reject(error);
		},
	);

	const getKey = (conf) => conf.baseURL + conf.url + conf.method + conf.query;

	/**
	 * Axios Response Interceptor
	 */
	axios.interceptors.response.use(
		function (response) {
			requests = requests.filter((r) => r !== getKey(response.config));
			return Promise.resolve(response);
		},
		function (error) {
			if (error.config) requests = requests.filter((r) => r !== getKey(error.config));
			return Promise.reject(error);
		},
	);

	axios.interceptors.response.use(
		function (response) {
			return response.data;
		},
		async function (error) {
			if (error?.config?.headers) delete error.config.headers["Authorization"];

			if (error.response && error.response.status === 401) {
				if (isNot401TokenExpiredError(error)) {
					return Promise.reject(error);
				}

				// If we've *already* retried this specific request after a complete re-auth attempt, the new token didn't help.
				// Clear state and force a fresh login.
				if (error.config.__didReauth === true) {
					const userStore = useUserStore();
					console.warn(
						"[Auth] Received another 40105/40106 after a re-auth. Rejecting request and clearing customer to force re-login.",
					);
					userStore.clearCustomer();
					userStore.attemptedLogin = true;
					return Promise.reject(error);
				}

				// If we've already retried once, just return early
				if (error.config.__isRetry) {
					return Promise.reject(error);
				}

				// Mark this request as "retry in progress" so if 401 again, we skip
				error.config.__isRetry = true;

				// This will happen if the user is logged in to Telenor ID+, but the Apigee token is expired
				console.log("Access token is expired");

				await telenorid.properlyRemoveUser();

				// If no reauth is in progress, start one
				if (!reauthPromise) {
					console.log("Creating reauthentication promise");
					const userStore = useUserStore();
					reauthPromise = telenorid
						.signinSilent()
						.then((user) => {
							console.log("Reauthentication successful");
							if (user.access_token) {
								console.log("Setting user data");
								userStore.setUser(JSON.parse(JSON.stringify(user)));
							} else {
								console.error("Reauthentication failed, no access token received, clearing user data");
								userStore.clearCustomer();
							}
						})
						.catch((reauthError) => {
							userStore.clearCustomer();
							throw reauthError; // re-throw so that await reauthPromise rejects
						})
						.finally(() => {
							error.config.__didReauth = true; // Mark that we've retried this request after re-auth
							reauthPromise = null;
							userStore.attemptedLogin = true;
						});
				}

				// Wait on (new or existing) reauthPromise
				try {
					await reauthPromise;
					// Resend the request when the user is reauthed, the attach-bearer interceptor will set the new token
					// Since we passed the same error.config, it will contain the fields __didReauth and __isRetry set in this interceptor
					return axios(error.config);
				} catch (reauthError) {
					console.error("Reauthentication failed", reauthError, "Clearing user data, login required");
					return Promise.reject(error);
				}
			}
			return Promise.reject(error);
		},
	);

	/**
	 * Checks if the given 401 error is unrelated to an expired access token.
	 */
	function isNot401TokenExpiredError(error) {
		const { data: errorData } = error.response;
		let parsedData = errorData;

		try {
			if (typeof errorData === "string") {
				parsedData = JSON.parse(errorData);
			}
		} catch (parseError) {
			console.error("Unable to parse error response:", parseError);
		}

		const errorCode = parsedData?.errorCode ?? null;
		const errorDescription = parsedData?.description ?? null;

		const tokenExpiryCodes = [40106, "40106", 40105, "40105"];

		const notTokenExpiry = errorCode && !tokenExpiryCodes.includes(errorCode);

		if (notTokenExpiry) {
			console.log(
				`The 401 response is unrelated to access token expiry. errorCode: ${errorCode}${
					errorDescription ? ` - ${errorDescription}` : ""
				}`,
			);
			return true;
		}

		return false;
	}

	return axios;
}
